import React, {
  Fragment,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'
// reason: FIXME at moment we rely too much on custom styling, we need all accessibilites from menu item but own styling
// eslint-disable-next-line @dhl/dhl-ui/no-restricted-imports
import { Collapse, MenuItem, MenuList, Popper } from '@mui/material'
import { useTheme } from '@dhl-ui/theme'
import { List, Box, useIsMobileVariant, Paper, Typography, BadgeInline, StackRow, Divider } from '@dhl-ui/components'
import { usePage } from '../../context'
import { type PageMenuItem } from './types'
import { hasSubMenu } from './helpers/has-submenu'
import { getActiveTopLevel } from './helpers/get-active-top-level'
import { MainMenuItem } from './MainMenuItem'
import { Toggle } from './Toggle'
import { SubMenuItem } from './SubMenuItem'

type Props = {
  /**
   * Navigational items
   */
  items: PageMenuItem[]

  /**
   * When you need something below the main menu
   */
  endNode?: ReactNode

  /**
   * Tell consumer which item the user has selected so they can navigate or do their own logic
   */
  onSelect: (arg: PageMenuItem) => void
}

type CollapsedMenuState = {
  anchorEl: HTMLElement
  itemKey: string
  shouldFocus: boolean
} | null

const mainMenuListTestId = 'page-side-menu-list'
const mainMenuListSelector = `[data-testid="${mainMenuListTestId}"]`
const submenuPopperId = 'submenu-menu'
const submenuPopperSelector = `#${submenuPopperId}`

/**
 * Our main menu is accessible when user uses keyboard navigation or mouse
 * because of its non expanded requirements you see a lot of events :( unfortunately
 */
export const MainMenu = ({ items: mainMenuItems, endNode, onSelect }: Props) => {
  const { spacing } = useTheme()
  const { isExpanded, setIsOpen } = usePage()
  const isMobile = useIsMobileVariant()

  const [openMenu, setOpenMenu] = useState<string | null>(() => {
    const matched = getActiveTopLevel(mainMenuItems)
    return matched ? matched?.key : null
  })

  const isMenuOpen = (item: PageMenuItem) => openMenu === item.key

  const [collapsedMenu, setCollapsedMenu] = useState<CollapsedMenuState>(null)
  const mainMenuRef = useRef<HTMLDivElement>(null)
  const nextMainItemKeyRef = useRef<string | null>(null)
  const submenuCloseTimeoutRef = useRef<number | null>(null)
  const open = Boolean(collapsedMenu)

  /**
   * When our submenu is open when collapsed and then we expand we do not want to have it open even when its not showing
   * because after collapsing again the position is gone
   */
  useEffect(() => {
    if (open && isExpanded) {
      setCollapsedMenu(null)
    }
  }, [isExpanded, open])

  const openCollapsedMenu = (event: React.SyntheticEvent<HTMLElement>, itemKey: string, shouldFocus: boolean) => {
    event.preventDefault()
    setCollapsedMenu({ anchorEl: event.currentTarget, itemKey, shouldFocus })
  }

  const handleClose = () => {
    if (submenuCloseTimeoutRef.current) {
      window.clearTimeout(submenuCloseTimeoutRef.current)
      submenuCloseTimeoutRef.current = null
    }
    setCollapsedMenu(null)
  }

  const cancelSubmenuClose = () => {
    if (submenuCloseTimeoutRef.current) {
      window.clearTimeout(submenuCloseTimeoutRef.current)
      submenuCloseTimeoutRef.current = null
    }
  }

  const focusMenuItem = () => {
    const itemKey = nextMainItemKeyRef.current
    nextMainItemKeyRef.current = null

    if (itemKey) {
      document.getElementById(itemKey)?.focus()
    }
  }

  const handleItemClick = (item: PageMenuItem, parentKey?: string) => (e: MouseEvent<HTMLElement>) => {
    // we render href inside our links, but just so user can open it in new tab
    e.preventDefault()
    e.stopPropagation()

    // Only set focus to next main menu item if clicking on a main menu item (no parentKey)
    // When clicking a submenu item, don't focus the next item to avoid triggering its hover state
    nextMainItemKeyRef.current = isExpanded || !parentKey ? item.key : null

    // user can click on an already active item again, we make sure correct item is open
    if (item.isActive) {
      // reason: done explicitly
      // eslint-disable-next-line n/no-sync
      flushSync(() => setOpenMenu(null))
      // reason: done explicitly
      // eslint-disable-next-line n/no-sync
      flushSync(() => setOpenMenu(getActiveTopLevel(mainMenuItems)?.key || null))
    }

    // make sure we close the menu when we are on mobile...
    setIsOpen(false)
    handleClose()

    onSelect(item)

    requestAnimationFrame(() => {
      requestAnimationFrame(focusMenuItem)
    })
  }

  const handleMainMenuItemClick = (item: PageMenuItem) => (event: MouseEvent<HTMLElement>) => {
    event.preventDefault()

    if (isExpanded) {
      if (isMenuOpen(item)) {
        setOpenMenu(null)
      } else {
        setOpenMenu(item.key)
      }
    } else {
      openCollapsedMenu(event, item.key, true)
    }
  }

  const handleMainMenuItemFocus = (item: PageMenuItem) => (event: FocusEvent<HTMLElement>) => {
    if (!isExpanded && hasSubMenu(item)) {
      openCollapsedMenu(event, item.key, false)
    } else if (!isExpanded) {
      handleClose()
    }
  }

  const handleMainMenuItemHover = (item: PageMenuItem) => (event: MouseEvent<HTMLElement>) => {
    cancelSubmenuClose()

    if (!isExpanded && hasSubMenu(item)) {
      openCollapsedMenu(event, item.key, false)
    } else if (!isExpanded) {
      handleClose()
    }
  }

  const handleMainMenuItemKeyDown = (item: PageMenuItem) => (event: KeyboardEvent<HTMLElement>) => {
    if (isExpanded || !hasSubMenu(item) || (event.key !== 'ArrowDown' && event.key !== 'ArrowRight')) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    if (collapsedMenu?.itemKey === item.key) {
      document.querySelector<HTMLElement>('[role="menu"] [role="menuitem"]')?.focus()
      return
    }

    openCollapsedMenu(event, item.key, true)
  }

  const handleMainMenuBlur = () => {
    requestAnimationFrame(() => {
      const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null
      const isInMainMenu = Boolean(activeElement && mainMenuRef.current?.contains(activeElement))
      const isInSubmenu = activeElement?.closest(submenuPopperSelector)

      if (!isInMainMenu && !isInSubmenu) {
        handleClose()
      }
    })
  }

  const handleMainMenuMouseLeave = (event: MouseEvent<HTMLElement>) => {
    const { clientX, clientY } = event

    requestAnimationFrame(() => {
      const hoveredElement = document.elementFromPoint(clientX, clientY)

      const isInMainMenu = hoveredElement?.closest(mainMenuListSelector)
      const isInSubmenu = hoveredElement?.closest(submenuPopperSelector)

      if (!isInMainMenu && !isInSubmenu) {
        // We consider 500ms to be sufficient for people to reach the submenu after leaving the main menu
        // they have to move 3 spacing tokens :)
        submenuCloseTimeoutRef.current = window.setTimeout(handleClose, 500)
      }
    })
  }

  const handleCollapsedMenuKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Tab' || !collapsedMenu) {
      return
    }

    event.preventDefault()

    const currentIndex = mainMenuItems.findIndex((mainItem) => mainItem.key === collapsedMenu.itemKey)
    const nextMainItemKey = mainMenuItems[currentIndex + (event.shiftKey ? -1 : 1)]?.key

    handleClose()
    requestAnimationFrame(() => {
      if (nextMainItemKey) {
        document.getElementById(nextMainItemKey)?.focus()
      }
    })
  }

  return (
    <>
      {mainMenuItems.length ? (
        <Box ref={mainMenuRef} onBlur={handleMainMenuBlur} onMouseLeave={handleMainMenuMouseLeave}>
          <List component="div" data-testid={mainMenuListTestId} sx={{ mx: 1.5, mt: 1 }}>
            {mainMenuItems.map((item, i) => {
              if (hasSubMenu(item)) {
                const { items } = item
                const hasActiveChild = items?.some((subItem) => subItem.isActive)
                const id = item.key
                const amount = items?.length ?? 0

                const markAsActive = item.isActive || ((!isExpanded || !isMenuOpen(item)) && hasActiveChild)

                return (
                  <Fragment key={item.key}>
                    <MainMenuItem
                      id={id}
                      icon={item.icon}
                      label={item.label}
                      link={item.link}
                      onClick={handleMainMenuItemClick(item)}
                      onFocus={handleMainMenuItemFocus(item)}
                      onHover={handleMainMenuItemHover(item)}
                      onKeyDown={handleMainMenuItemKeyDown(item)}
                      isActive={markAsActive}
                      isDisabled={item.isDisabled}
                      isOpen={isMenuOpen(item)}
                      isExpanded={isExpanded}
                      isExpandable
                      isCollapsedMenuOpenForItem={collapsedMenu?.itemKey === item.key}
                    />

                    {isExpanded ? (
                      <Collapse in={isMenuOpen(item)} unmountOnExit sx={{ mb: 1 }}>
                        <Box sx={{ pl: isExpanded ? 2 : 0, pb: 1 }}>
                          {item.items?.map((subItem) => (
                            <SubMenuItem
                              id={subItem.key}
                              aria-labelledby={id}
                              key={subItem.key}
                              label={subItem.label}
                              link={subItem.link}
                              badge={subItem.badge}
                              onClick={handleItemClick(subItem, id)}
                              isActive={subItem.isActive}
                              isDisabled={subItem.isDisabled}
                            />
                          ))}
                        </Box>
                      </Collapse>
                    ) : (
                      <Popper
                        id={submenuPopperId}
                        open={open && collapsedMenu?.itemKey === item.key}
                        anchorEl={collapsedMenu?.anchorEl ?? null}
                        placement="right-start"
                        sx={{ ml: 23, pointerEvents: 'none', zIndex: 'modal' }}
                      >
                        <Paper
                          elevation="sm"
                          isRounded
                          isOutlined
                          onMouseEnter={cancelSubmenuClose}
                          sx={{ minWidth: spacing(24), ml: 3, pointerEvents: 'auto' }}
                        >
                          <MenuList
                            aria-labelledby={id}
                            autoFocus={collapsedMenu?.shouldFocus}
                            autoFocusItem={collapsedMenu?.shouldFocus}
                            onKeyDown={handleCollapsedMenuKeyDown}
                            tabIndex={collapsedMenu?.shouldFocus ? undefined : -1}
                          >
                            {items?.map((subItem, j) => (
                              <MenuItem
                                data-testid={`page-side-menu-${subItem.key}`}
                                key={subItem.key}
                                onClick={handleItemClick(subItem, id)}
                                selected={subItem.isActive}
                                divider={amount - 1 !== j}
                                sx={({ misc }) => ({
                                  fontWeight: subItem.isActive ? 'bold' : undefined,
                                  color: subItem.isActive ? 'components.navbar.contrast' : 'grey.700',
                                  bgcolor: subItem.isActive ? 'components.navbar.selected' : 'background.paper',

                                  '&.Mui-selected': {
                                    opacity: 1,
                                    // ensure correct background color for the selected state:
                                    bgcolor: subItem.isActive ? 'components.navbar.selected' : undefined,

                                    '&:hover': {
                                      bgcolor: subItem.isActive ? 'components.navbar.selectedHover' : 'action.hover',
                                    },
                                  },

                                  '&.Mui-selected.Mui-focusVisible, &.Mui-focusVisible': {
                                    outline: misc.borderStyle('info.main', 3),
                                    outlineOffset: misc.borderSize(-3),
                                    backgroundColor: subItem.isActive
                                      ? 'components.navbar.selectedHover'
                                      : 'action.hover',
                                  },
                                })}
                              >
                                <StackRow>
                                  <Box>{subItem.label}</Box>
                                  {subItem.badge && (
                                    <BadgeInline
                                      variant="standard"
                                      badgeContent={subItem.badge.count}
                                      color={subItem.badge.color}
                                    />
                                  )}
                                </StackRow>
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Paper>
                      </Popper>
                    )}
                  </Fragment>
                )
              }

              return (
                <Fragment key={item.key}>
                  {item.sectionTitle &&
                    // reason: we have two states but still readable
                    // eslint-disable-next-line no-nested-ternary
                    (isExpanded ? (
                      <Typography variant="h6" sx={{ mt: i === 0 ? 0 : 1, p: spacing(1.75, 2.25, 1, 2.25) }}>
                        {isExpanded ? item.sectionTitle : ''}
                      </Typography>
                    ) : i !== 0 ? (
                      <Divider sx={{ mb: 0.5 }} />
                    ) : null)}

                  <MainMenuItem
                    id={item.key}
                    key={item.key}
                    icon={item.icon}
                    label={item.label}
                    badge={item.badge}
                    link={item.link}
                    onClick={handleItemClick(item)}
                    onFocus={handleMainMenuItemFocus(item)}
                    onHover={handleMainMenuItemHover(item)}
                    isActive={item.isActive}
                    isDisabled={item.isDisabled}
                    isExpanded={isExpanded}
                  />
                </Fragment>
              )
            })}
          </List>
        </Box>
      ) : null}
      <Box sx={{ mt: 'auto' }}>{endNode}</Box>
      {!isMobile ? <Toggle /> : null}
    </>
  )
}
